import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import { getCrmDispositions, getCrmLeads, logCrmQuickDisposition } from '../config/api';
import CrmCallDialog from '../components/CrmCallDialog';
import { hasCrmManagerAccess } from '../utils/crmAccess';

const STAGE_LABELS = {
  not_contacted: 'Not contacted',
  contacted: 'Contacted',
  interested: 'Interested',
  app_registered: 'App registered',
  active: 'Active',
  inactive: 'Inactive',
};

// The three no-connect quick dispositions (spec §8).
const DEFAULT_QUICK_DISPOSITIONS = [
  { value: 'did_not_answer', label: 'DNA' },
  { value: 'switched_off', label: 'Switched off' },
  { value: 'not_reachable', label: 'Not reachable' },
];

const money = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const isOverdue = (d) => {
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(d) < today;
};

const BUSINESS_TIME_ZONE = 'Asia/Kolkata';
const todayStr = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

const CrmCalling = () => {
  const currentUser = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  const includesOwnCallHistory = hasCrmManagerAccess(currentUser);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyLeadId, setBusyLeadId] = useState(null);
  const [callTarget, setCallTarget] = useState(null);
  const [quickDispositions, setQuickDispositions] = useState(DEFAULT_QUICK_DISPOSITIONS);

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#f8f9fa',
        fontWeight: '600',
        fontSize: '11px',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        padding: '8px 6px',
      },
    },
    cells: { style: { padding: '8px 6px', fontSize: '12px' } },
    rows: {
      style: { '&:hover': { backgroundColor: '#f8f9fa' }, minHeight: '48px' },
    },
  };

  useEffect(() => {
    loadQueue();
    getCrmDispositions()
      .then((response) => {
        const configured = (response?.data?.dispositions || [])
          .filter((row) => row.isQuick)
          .map((row) => ({ value: row.key, label: row.label }));
        if (configured.length) setQuickDispositions(configured);
      })
      .catch(() => {});
    // The queue loader intentionally runs once on entry; it is refreshed after
    // actions through the existing call/disposition handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQueue = async () => {
    setLoading(true);
    try {
      // Base queue = assigned Leads due today or earlier. Manager/SuperAdmin
      // additionally request distinct Leads they personally called.
      const res = await getCrmLeads({
        assigned_to_me: true,
        follow_up_before: todayStr(),
        ...(includesOwnCallHistory ? { include_my_calls: true } : {}),
        limit: 200,
        page: 1,
      });
      const data = res?.data || {};
      setLeads(data.leads || []);
    } catch (err) {
      console.error('Failed to load calling queue:', err);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickDisposition = async (lead, disposition) => {
    setBusyLeadId(lead.id);
    try {
      await logCrmQuickDisposition(lead.id, {
        disposition,
        contactId: lead.primaryContactId,
      });
      // The lead's follow-up moves to tomorrow, so it drops out of today's queue.
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
    } catch (err) {
      alert(err.message || 'Could not log the attempt.');
    } finally {
      setBusyLeadId(null);
    }
  };

  const getStageBadge = (stage) => {
    const cls =
      stage === 'active'
        ? 'status-badge status-confirmed'
        : stage === 'inactive'
        ? 'status-badge status-pending'
        : 'status-badge';
    return <span className={cls}>{STAGE_LABELS[stage] || stage}</span>;
  };

  const columns = useMemo(
    () => [
      {
        name: 'Lead',
        selector: (row) => row.displayName || '',
        cell: (row) => (
          <div>
            <Link to={`/crm/leads/${row.id}`} style={{ color: '#0d6efd', fontWeight: 600 }}>
              {row.displayName || `Lead #${row.id}`}
            </Link>
            {row.unreadWhatsapp > 0 && <span className="crm-wa-badge">WA {row.unreadWhatsapp}</span>}
            {row.primaryContactName && <div className="crm-muted">{row.primaryContactName}</div>}
          </div>
        ),
        minWidth: '190px',
      },
      {
        name: 'Stage',
        selector: (row) => row.stage,
        cell: (row) => getStageBadge(row.stage),
      },
      {
        name: 'Brands bought',
        // Concatenated top brands so the caller knows what the client buys
        // before the call connects (spec §8).
        selector: (row) => (row.brandsBought || []).join(', ') || '—',
        minWidth: '160px',
      },
      {
        name: 'Orders 30 / 90 D',
        cell: (row) => {
          const o = row.orders || {};
          return (
            <div>
              <div>{`${o.count30 || 0} / ${o.count90 || 0}`}</div>
              {o.value90 != null && <div className="crm-muted">{money(o.value90)}</div>}
            </div>
          );
        },
        minWidth: '120px',
      },
      {
        name: 'Follow-up',
        selector: (row) => row.followUpDate || '',
        cell: (row) => {
          if (!row.followUpDate) return <span className="crm-muted">none</span>;
          const overdue = isOverdue(row.followUpDate);
          return (
            <span className={overdue ? 'crm-overdue' : ''}>
              {new Date(row.followUpDate).toLocaleDateString()}
              {overdue ? ' (overdue)' : ''}
            </span>
          );
        },
      },
      {
        name: 'Actions',
        cell: (row) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                className="btn btn-success crm-btn-xs"
                disabled={!row.callable}
                title={
                  row.callable
                    ? 'Call the primary contact'
                    : 'Primary contact is not registered in the app — calling not available'
                }
                onClick={() => row.callable && setCallTarget(row)}
              >
                Call
              </button>
              <Link to={`/crm/leads/${row.id}`} className="btn btn-secondary crm-btn-xs">
                Open
              </Link>
            </div>
            <div className="crm-quick-dispo">
              {quickDispositions.map((q) => (
                <button
                  key={q.value}
                  className="btn"
                  disabled={busyLeadId === row.id}
                  onClick={() => handleQuickDisposition(row, q.value)}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        ),
        minWidth: '210px',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyLeadId, quickDispositions]
  );

  return (
    <div className="container">
      <div className="crm-list-header">
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>Calling view</h2>
          <div className="crm-list-count">
            {leads.length} lead{leads.length === 1 ? '' : 's'} · {includesOwnCallHistory
              ? 'assigned to you and due, or previously called by you.'
              : 'assigned to you with follow-up today or earlier.'}
            {!includesOwnCallHistory && ' A lead leaves after its call outcome is saved.'}
          </div>
        </div>
        <Link to="/crm/leads" className="btn btn-secondary">All leads</Link>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={leads}
          progressPending={loading}
          customStyles={customStyles}
          highlightOnHover
          noDataComponent={<div style={{ padding: '24px' }}>Your calling queue is clear.</div>}
        />
      </div>

      {callTarget && (
        <CrmCallDialog
          leadId={callTarget.id}
          contact={{ id: callTarget.primaryContactId, name: callTarget.primaryContactName }}
          hubLinks={callTarget.hubLinks || []}
          onClose={() => setCallTarget(null)}
          onLogged={() => {
            setCallTarget(null);
            loadQueue();
          }}
        />
      )}
    </div>
  );
};

export default CrmCalling;
